/**
 * Alshallal Pastry Company - Create Employee Page
 * Phase 2: User & Employee Management
 * Phase 24.8: Added manager and department dropdowns
 * Phase 24.14: Full nationalities list
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  User,
  Briefcase,
  CreditCard,
  Building2,
  Loader2,
  AlertCircle,
  Users
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { NATIONALITIES } from '@/constants/nationalities';
import { ManagerSelect } from '@/components/employees/ManagerSelect';
import { EmployeeFormField } from '@/components/employees/EmployeeFormField';
import { useEmployeeFormDropdowns } from '@/hooks/useEmployeeFormDropdowns';
import {
  CONTRACT_TYPES,
  GENDERS,
  DEPARTMENTS,
  BANKS,
} from '@/constants/employeeForm';
import { AnnualLeaveFields } from '@/components/employees/AnnualLeaveFields';
import {
  validateEmployeeForm,
  firstTabWithError,
} from '@/lib/employeeFormValidation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EmployeeCreatePage = () => {
  const navigate = useNavigate();
  const { token, hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    // Personal
    first_name: '',
    first_name_ar: '',
    middle_name: '',
    middle_name_ar: '',
    last_name: '',
    last_name_ar: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    phone: '',
    personal_email: '',
    address: '',
    city: '',
    // Identity
    national_id: '',
    national_id_expiry: '',
    iqama: '',
    iqama_expiry: '',
    passport_number: '',
    passport_expiry: '',
    // Employment
    employee_number: '',
    job_title: '',
    job_title_ar: '',
    department: '',
    department_ar: '',
    department_id: '',  // Phase 24.8: Department ID for dropdown
    branch_id: '',  // Phase 24.8: Branch ID
    hire_date: '',
    contract_type: 'permanent',
    contract_start_date: '',
    contract_end_date: '',
    location: '',
    daily_working_hours: '',  // Phase 22: Required field - empty requires explicit selection
    manager_id: '',  // Phase 24.8: Direct Manager
    approving_manager_id: '',  // Phase 24.8: Approving Manager
    // Annual leave (2026-09-17) — per-employee entitlement + accrual dates
    annual_leave_days: '',
    annual_leave_cycle_years: '1',
    annual_leave_change_reason: '',
    annual_leave_days_justification: '',
    last_annual_leave_start_date: '',
    last_annual_leave_rejoin_date: '',
    // Bank
    bank_name: '',
    iban: '',
    account_holder_name: '',
    // Compensation
    basic_salary: '',
    housing_allowance: '',
    transportation_allowance: '',
    other_allowances: '',
  });

  // Shared dropdown data + inline branch creation (extracted to hook)
  const {
    managers: employees,
    departmentsList,
    branchesList,
    createBranch,
  } = useEmployeeFormDropdowns(token);
  const [newBranchName, setNewBranchName] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);

  const getLabel = (item) => language === 'ar' ? item.label.ar : item.label.en;

  // Phase 24.8: Create new branch inline
  const handleCreateBranch = async () => {
    if (!newBranchName.trim()) return;
    setCreatingBranch(true);
    try {
      const newBranch = await createBranch(newBranchName);
      if (newBranch) {
        handleChange('branch_id', newBranch.id);
        setNewBranchName('');
      }
    } catch (err) {
      console.error('Failed to create branch:', err);
    } finally {
      setCreatingBranch(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors = validateEmployeeForm(formData, { isRTL });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isRTL]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const tab = firstTabWithError(validateEmployeeForm(formData, { isRTL }));
      if (tab) setActiveTab(tab);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        personal: {
          first_name: formData.first_name || formData.first_name_ar || '-',
          first_name_ar: formData.first_name_ar || null,
          middle_name: formData.middle_name || null,
          middle_name_ar: formData.middle_name_ar || null,
          last_name: formData.last_name || formData.last_name_ar || '-',
          last_name_ar: formData.last_name_ar || null,
          date_of_birth: formData.date_of_birth || null,
          gender: formData.gender || null,
          nationality: formData.nationality,
          phone: formData.phone,
          personal_email: formData.personal_email || null,
          address: formData.address || null,
          city: formData.city || null,
        },
        identity: {
          national_id: formData.national_id || null,
          national_id_expiry: formData.national_id_expiry || null,
          iqama: formData.iqama || null,
          iqama_expiry: formData.iqama_expiry || null,
          passport_number: formData.passport_number || null,
          passport_expiry: formData.passport_expiry || null,
        },
        employment: {
          employee_number: formData.employee_number || null,
          job_title: formData.job_title || formData.job_title_ar || '-',
          job_title_ar: formData.job_title_ar || null,
          department: formData.department,
          department_ar: formData.department_ar || null,
          department_id: formData.department_id || null,  // Phase 24.8
          branch_id: formData.branch_id || null,  // Phase 24.8: Branch
          hire_date: formData.hire_date,
          contract_type: formData.contract_type,
          contract_start_date: formData.contract_start_date || null,
          contract_end_date: formData.contract_end_date || null,
          location: formData.location || null,
          status: 'active',
          daily_working_hours: parseInt(formData.daily_working_hours) || 8,  // Phase 22
          manager_id: formData.manager_id || null,  // Phase 24.8: Direct Manager
          approving_manager_id: formData.approving_manager_id || null,  // Phase 24.8: Approving Manager
          // Annual leave (2026-09-17) — only HR Manager / HR Admin / Super
          // Admin can reach these inputs, and the backend refuses them from
          // anybody else.
          annual_leave_days: formData.annual_leave_days
            ? parseInt(formData.annual_leave_days, 10)
            : null,
          annual_leave_days_justification: formData.annual_leave_days_justification || null,
          annual_leave_cycle_years: formData.annual_leave_days ? Number(formData.annual_leave_cycle_years || 1) : null,
          last_annual_leave_start_date: formData.last_annual_leave_start_date || null,
          last_annual_leave_rejoin_date: formData.last_annual_leave_rejoin_date || null,
        },
        bank: formData.iban ? {
          bank_name: formData.bank_name || null,
          iban: formData.iban.replace(/\s/g, '').toUpperCase(),
          account_holder_name: formData.account_holder_name || null,
        } : null,
        compensation: (formData.basic_salary || formData.housing_allowance) ? {
          basic_salary: parseFloat(formData.basic_salary) || null,
          housing_allowance: parseFloat(formData.housing_allowance) || null,
          transportation_allowance: parseFloat(formData.transportation_allowance) || null,
          other_allowances: parseFloat(formData.other_allowances) || null,
        } : null,
      };

      await axios.post(`${API_URL}/employees/?reason=${encodeURIComponent(formData.annual_leave_change_reason || '')}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/employees');
    } catch (err) {
      console.error('Failed to create employee:', err);
      const errorMsg = err.response?.data?.detail?.error || err.response?.data?.detail || 'Failed to create employee';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field, label, type = 'text', options = {}) => (
    <EmployeeFormField
      field={field}
      label={label}
      type={type}
      value={formData[field]}
      onChange={handleChange}
      error={errors[field]}
      required={options.required}
      disabled={options.disabled}
      placeholder={options.placeholder}
    />
  );

  return (
    <div className="space-y-6" data-testid="employee-create-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/employees')}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'إضافة موظف جديد' : 'Add New Employee'}
          </h1>
          <p className="text-slate-600 mt-1">
            {isRTL ? 'أدخل بيانات الموظف الجديد' : 'Enter new employee information'}
          </p>
        </div>
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-700 font-medium">
              {isRTL ? 'فشل إنشاء الموظف' : 'Failed to create employee'}
            </p>
            <p className="text-red-600 text-sm mt-1">{errors.submit}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-5">
            <TabsTrigger value="personal" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'شخصي' : 'Personal'}</span>
            </TabsTrigger>
            <TabsTrigger value="identity" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'الهوية' : 'Identity'}</span>
            </TabsTrigger>
            <TabsTrigger value="employment" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'الوظيفة' : 'Employment'}</span>
            </TabsTrigger>
            <TabsTrigger value="bank" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">{isRTL ? 'البنك' : 'Bank'}</span>
            </TabsTrigger>
            <TabsTrigger value="compensation" className="hidden lg:flex items-center gap-2">
              <span>{isRTL ? 'التعويضات' : 'Compensation'}</span>
            </TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'المعلومات الشخصية' : 'Personal Information'}</CardTitle>
                <CardDescription>
                  {isRTL ? 'الاسم الأول (بالإنجليزية أو العربية) مطلوب' : 'First name (English or Arabic) is required'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('first_name', isRTL ? 'الاسم الأول (English)' : 'First Name (English)', 'text', { required: !formData.first_name_ar })}
                  {renderField('first_name_ar', isRTL ? 'الاسم الأول (عربي)' : 'First Name (Arabic)', 'text', { required: !formData.first_name })}
                  {renderField('middle_name', isRTL ? 'الاسم الأوسط (English)' : 'Middle Name (English)')}
                  {renderField('middle_name_ar', isRTL ? 'الاسم الأوسط (عربي)' : 'Middle Name (Arabic)')}
                  {renderField('last_name', isRTL ? 'اسم العائلة (English)' : 'Last Name (English)', 'text', { required: !formData.last_name_ar })}
                  {renderField('last_name_ar', isRTL ? 'اسم العائلة (عربي)' : 'Last Name (Arabic)', 'text', { required: !formData.last_name })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {renderField('date_of_birth', isRTL ? 'تاريخ الميلاد' : 'Date of Birth', 'date')}
                  
                  <div className="space-y-2">
                    <Label>{isRTL ? 'الجنس' : 'Gender'}</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger data-testid="select-gender">
                        <SelectValue placeholder={isRTL ? 'اختر' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => (
                          <SelectItem key={g.value} value={g.value}>{getLabel(g)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className={cn(errors.nationality && 'text-red-500')}>
                      {isRTL ? 'الجنسية' : 'Nationality'}
                      <span className="text-red-500 ms-1">*</span>
                    </Label>
                    <Select 
                      value={formData.nationality} 
                      onValueChange={(v) => handleChange('nationality', v)}
                    >
                      <SelectTrigger 
                        className={cn(errors.nationality && 'border-red-500')}
                        data-testid="select-nationality"
                      >
                        <SelectValue placeholder={isRTL ? 'اختر الجنسية' : 'Select nationality'} />
                      </SelectTrigger>
                      <SelectContent>
                        {NATIONALITIES.map(n => (
                          <SelectItem key={n.value} value={n.value}>{getLabel(n)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.nationality && (
                      <p className="text-sm text-red-500">{errors.nationality}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('phone', isRTL ? 'رقم الجوال' : 'Mobile Number', 'tel', { required: true, placeholder: '05XXXXXXXX' })}
                  {renderField('personal_email', isRTL ? 'البريد الإلكتروني' : 'Personal Email', 'email', { placeholder: 'email@example.com' })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('address', isRTL ? 'العنوان' : 'Address')}
                  {renderField('city', isRTL ? 'المدينة' : 'City')}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Identity Tab */}
          <TabsContent value="identity" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'وثائق الهوية' : 'Identity Documents'}</CardTitle>
                <CardDescription>
                  {isRTL 
                    ? 'الهوية الوطنية للسعوديين أو الإقامة لغير السعوديين (مطلوب)' 
                    : 'National ID for Saudis or Iqama for non-Saudis (required)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* National ID Section */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700">
                    {isRTL ? 'الهوية الوطنية (للسعوديين)' : 'National ID (Saudi Nationals)'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('national_id', isRTL ? 'رقم الهوية الوطنية' : 'National ID Number', 'text', { 
                      required: formData.nationality === 'saudi',
                      placeholder: '1XXXXXXXXX',
                      disabled: formData.nationality === 'non_saudi'
                    })}
                    {renderField('national_id_expiry', isRTL ? 'تاريخ انتهاء الهوية' : 'National ID Expiry', 'date', {
                      disabled: formData.nationality === 'non_saudi'
                    })}
                  </div>
                </div>

                {/* Iqama Section */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700">
                    {isRTL ? 'الإقامة (لغير السعوديين)' : 'Iqama (Non-Saudi Nationals)'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('iqama', isRTL ? 'رقم الإقامة' : 'Iqama Number', 'text', {
                      required: formData.nationality === 'non_saudi',
                      placeholder: '2XXXXXXXXX',
                      disabled: formData.nationality === 'saudi'
                    })}
                    {renderField('iqama_expiry', isRTL ? 'تاريخ انتهاء الإقامة' : 'Iqama Expiry', 'date', {
                      disabled: formData.nationality === 'saudi'
                    })}
                  </div>
                </div>

                {/* Passport Section */}
                <div className="p-4 border rounded-lg space-y-4">
                  <h4 className="font-medium text-slate-700">
                    {isRTL ? 'جواز السفر (اختياري)' : 'Passport (Optional)'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {renderField('passport_number', isRTL ? 'رقم جواز السفر' : 'Passport Number')}
                    {renderField('passport_expiry', isRTL ? 'تاريخ انتهاء الجواز' : 'Passport Expiry', 'date')}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'معلومات التوظيف' : 'Employment Information'}</CardTitle>
                <CardDescription>
                  {isRTL ? 'تاريخ التعيين والقسم والمسمى الوظيفي (مطلوب)' : 'Hire date, department, and job title (required)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('employee_number', isRTL ? 'رقم الموظف' : 'Employee Number', 'text', { placeholder: 'EMP001' })}
                  {renderField('hire_date', isRTL ? 'تاريخ التعيين' : 'Hire Date', 'date', { required: true })}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('job_title', isRTL ? 'المسمى الوظيفي (English)' : 'Job Title (English)', 'text', { required: !formData.job_title_ar })}
                  {renderField('job_title_ar', isRTL ? 'المسمى الوظيفي (عربي)' : 'Job Title (Arabic)')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className={cn(errors.department && 'text-red-500')}>
                      {isRTL ? 'القسم' : 'Department'}
                      <span className="text-red-500 ms-1">*</span>
                    </Label>
                    <Select 
                      value={formData.department_id || formData.department} 
                      onValueChange={(v) => {
                        // Find department name for legacy compatibility
                        const dept = departmentsList.find(d => d.id === v);
                        if (dept) {
                          handleChange('department_id', v);
                          handleChange('department', dept.name_en || dept.name_ar);
                          handleChange('department_ar', dept.name_ar || dept.name_en);
                        } else {
                          handleChange('department', v);
                        }
                      }}
                    >
                      <SelectTrigger 
                        className={cn(errors.department && 'border-red-500')}
                        data-testid="select-department"
                      >
                        <SelectValue placeholder={isRTL ? 'اختر القسم' : 'Select department'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {departmentsList.length > 0 ? (
                          departmentsList.map(dept => (
                            <SelectItem 
                              key={dept.id} 
                              value={dept.id}
                              className={cn(
                                dept.isMain && "font-semibold bg-slate-50",
                                dept.level === 1 && "ps-6 text-sm"
                              )}
                            >
                              {dept.level === 1 && "└ "}
                              {isRTL ? (dept.name_ar || dept.name_en) : (dept.name_en || dept.name_ar)}
                              {dept.code && <span className="text-slate-400 ms-2 text-xs">({dept.code})</span>}
                            </SelectItem>
                          ))
                        ) : (
                          // Fallback to hardcoded departments
                          DEPARTMENTS.map(dept => (
                            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {errors.department && (
                      <p className="text-sm text-red-500">{errors.department}</p>
                    )}
                  </div>
                  {renderField('department_ar', isRTL ? 'القسم (عربي)' : 'Department (Arabic)')}
                </div>

                {/* Phase 24.8: Manager Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>
                      <Users className="inline h-4 w-4 me-1" />
                      {isRTL ? 'المدير المباشر' : 'Direct Manager'}
                    </Label>
                    <ManagerSelect
                      value={formData.manager_id || ''}
                      onChange={(v) => handleChange('manager_id', v)}
                      employees={employees}
                      placeholder={isRTL ? 'اختر المدير' : 'Select manager'}
                      noneLabel={isRTL ? '— لا يوجد —' : '— None —'}
                      testId="select-manager"
                    />
                    <p className="text-xs text-slate-500">
                      {isRTL 
                        ? 'المدير المباشر لموافقات الإجازات والطلبات'
                        : 'Direct manager for leave and request approvals'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>
                      <Users className="inline h-4 w-4 me-1" />
                      {isRTL ? 'مدير الموافقات' : 'Approving Manager'}
                    </Label>
                    <ManagerSelect
                      value={formData.approving_manager_id || ''}
                      onChange={(v) => handleChange('approving_manager_id', v)}
                      employees={employees}
                      placeholder={isRTL ? 'اختر المدير' : 'Select manager'}
                      noneLabel={isRTL ? '— لا يوجد —' : '— None —'}
                      testId="select-approving-manager"
                    />
                    <p className="text-xs text-slate-500">
                      {isRTL 
                        ? 'اختياري - إذا كان مختلفاً عن المدير المباشر'
                        : 'Optional - If different from direct manager'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>{isRTL ? 'نوع العقد' : 'Contract Type'}</Label>
                    <Select 
                      value={formData.contract_type} 
                      onValueChange={(v) => handleChange('contract_type', v)}
                    >
                      <SelectTrigger data-testid="select-contract-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTRACT_TYPES.map(ct => (
                          <SelectItem key={ct.value} value={ct.value}>{getLabel(ct)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {renderField('contract_start_date', isRTL ? 'بداية العقد' : 'Contract Start', 'date')}
                  {renderField('contract_end_date', isRTL ? 'نهاية العقد' : 'Contract End', 'date')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('location', isRTL ? 'موقع العمل' : 'Work Location')}
                  
                  {/* Phase 22: Daily Working Hours */}
                  <div className="space-y-2">
                    <Label className={cn(errors.daily_working_hours && 'text-red-500')}>
                      {isRTL ? 'ساعات العمل اليومية' : 'Daily Working Hours'}
                      <span className="text-red-500 ms-1">*</span>
                    </Label>
                    <Select 
                      value={formData.daily_working_hours} 
                      onValueChange={(v) => handleChange('daily_working_hours', v)}
                    >
                      <SelectTrigger 
                        className={cn(errors.daily_working_hours && 'border-red-500')}
                        data-testid="select-daily-working-hours"
                      >
                        <SelectValue placeholder={isRTL ? 'اختر' : 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">{isRTL ? '8 ساعات/يوم' : '8 hours/day'}</SelectItem>
                        <SelectItem value="10">{isRTL ? '10 ساعات/يوم' : '10 hours/day'}</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.daily_working_hours && (
                      <p className="text-sm text-red-500">{errors.daily_working_hours}</p>
                    )}
                  </div>
                </div>

                {/* Phase 24.8: Branch Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>
                      <Building2 className="inline h-4 w-4 me-1" />
                      {isRTL ? 'الفرع' : 'Branch'}
                    </Label>
                    <Select 
                      value={formData.branch_id || ''} 
                      onValueChange={(v) => handleChange('branch_id', v === 'none' ? '' : v)}
                    >
                      <SelectTrigger data-testid="select-branch">
                        <SelectValue placeholder={isRTL ? 'اختر الفرع' : 'Select branch'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{isRTL ? '- لا يوجد -' : '- None -'}</SelectItem>
                        {branchesList.map(branch => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {isRTL ? (branch.name_ar || branch.name_en) : (branch.name_en || branch.name_ar)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {isRTL 
                        ? 'الفرع الذي يعمل فيه الموظف'
                        : 'The branch where the employee works'}
                    </p>
                  </div>

                  {/* Inline Branch Creation */}
                  <div className="space-y-2">
                    <Label>{isRTL ? 'إنشاء فرع جديد' : 'Create New Branch'}</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder={isRTL ? 'اسم الفرع الجديد' : 'New branch name'}
                        value={newBranchName}
                        onChange={(e) => setNewBranchName(e.target.value)}
                        data-testid="input-new-branch-name"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCreateBranch}
                        disabled={!newBranchName.trim() || creatingBranch}
                        data-testid="btn-create-branch"
                      >
                        {creatingBranch ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          isRTL ? 'إضافة' : 'Add'
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      {isRTL 
                        ? 'أو أنشئ فرعًا جديدًا إذا لم يكن موجودًا'
                        : 'Or create a new branch if not available'}
                    </p>
                  </div>
                </div>

                {/* Annual leave entitlement + accrual dates (2026-09-17).
                    HR Manager / HR Admin / Super Admin only. */}
                {hasRole('hr_manager', 'hr_admin', 'super_admin') && (
                  <AnnualLeaveFields
                    values={formData}
                    onChange={handleChange}
                    errors={errors}
                    isRTL={isRTL}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Tab */}
          <TabsContent value="bank" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'المعلومات البنكية' : 'Bank Information'}</CardTitle>
                <CardDescription>
                  {isRTL ? 'اختياري - للتحويلات البنكية' : 'Optional - for bank transfers'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{isRTL ? 'اسم البنك' : 'Bank Name'}</Label>
                    <Select 
                      value={formData.bank_name} 
                      onValueChange={(v) => handleChange('bank_name', v)}
                    >
                      <SelectTrigger data-testid="select-bank">
                        <SelectValue placeholder={isRTL ? 'اختر البنك' : 'Select bank'} />
                      </SelectTrigger>
                      <SelectContent>
                        {BANKS.map(bank => (
                          <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {renderField('iban', isRTL ? 'الآيبان' : 'IBAN', 'text', { placeholder: 'SA0000000000000000000000' })}
                </div>
                {renderField('account_holder_name', isRTL ? 'اسم صاحب الحساب' : 'Account Holder Name')}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compensation Tab */}
          <TabsContent value="compensation" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'التعويضات' : 'Compensation'}</CardTitle>
                <CardDescription>
                  {isRTL ? 'اختياري - للتخزين فقط (بدون معالجة الرواتب)' : 'Optional - storage only (no payroll processing)'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('basic_salary', isRTL ? 'الراتب الأساسي (ريال)' : 'Basic Salary (SAR)', 'number', { placeholder: '0.00' })}
                  {renderField('housing_allowance', isRTL ? 'بدل السكن (ريال)' : 'Housing Allowance (SAR)', 'number', { placeholder: '0.00' })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderField('transportation_allowance', isRTL ? 'بدل النقل (ريال)' : 'Transportation Allowance (SAR)', 'number', { placeholder: '0.00' })}
                  {renderField('other_allowances', isRTL ? 'بدلات أخرى (ريال)' : 'Other Allowances (SAR)', 'number', { placeholder: '0.00' })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/employees')}
            disabled={loading}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                {isRTL ? 'جاري الحفظ...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 me-2" />
                {isRTL ? 'حفظ الموظف' : 'Save Employee'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeCreatePage;
